import { z } from 'zod';

// User and Authentication Schemas
export const userProfileSchema = z.object({
  displayName: z.string().min(2, 'Name must be at least 2 characters').max(100, 'Name too long'),
  email: z.string().email('Invalid email address'),
  phone: z.string().optional(),
  address: z.string().optional(),
  roles: z.array(z.enum([
    'admin', 
    'treasurer', 
    'manager', 
    'delivery', 
    'cashier', 
    'general manager',
    'cooperative_store_manager',
    'member_store_manager', 
    'establishment_store_manager'
  ])).optional(),
  membershipStatus: z.enum(['pending', 'active', 'suspended', 'inactive']).optional(),
  shareCapital: z.number().min(0, 'Share capital cannot be negative').optional(),
  loanAmount: z.number().min(0, 'Loan amount cannot be negative').optional(),
});

export const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

export const signupSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
  displayName: z.string().min(2, 'Name must be at least 2 characters'),
  phone: z.string().optional(),
  address: z.string().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

// Product Schemas
export const productSchema = z.object({
  name: z.string().min(1, 'Product name is required').max(200, 'Product name too long'),
  sku: z.string().min(1, 'SKU is required').max(50, 'SKU too long'),
  description: z.string().optional(),
  price: z.number().min(0, 'Price cannot be negative'),
  stock: z.number().min(0, 'Stock cannot be negative'),
  category: z.string().min(1, 'Category is required'),
  imageUrl: z.string().url('Invalid image URL').optional(),
  isActive: z.boolean().optional(),
  kgPerUnit: z.number().min(0, 'Weight per unit cannot be negative').optional(),
  cooperativeFeePercentage: z.number().min(0, 'Fee percentage cannot be negative').max(100, 'Fee percentage cannot exceed 100%').optional(),
});

// Financial Schemas
export const accountSchema = z.object({
  code: z.string().min(1, 'Account code is required').max(20, 'Account code too long'),
  name: z.string().min(1, 'Account name is required').max(100, 'Account name too long'),
  type: z.enum(['Asset', 'Liability', 'Equity', 'Income', 'Expense'], {
    errorMap: () => ({ message: 'Invalid account type' })
  }),
  main: z.string().min(1, 'Main account is required'),
  individual: z.string().optional(),
  isActive: z.boolean().optional(),
});

export const journalEntrySchema = z.object({
  date: z.string().min(1, 'Date is required'),
  reference: z.string().min(1, 'Reference is required').max(100, 'Reference too long'),
  description: z.string().min(1, 'Description is required').max(500, 'Description too long'),
  lines: z.array(z.object({
    accountId: z.string().min(1, 'Account is required'),
    debit: z.number().min(0, 'Debit amount cannot be negative'),
    credit: z.number().min(0, 'Credit amount cannot be negative'),
  })).min(2, 'At least 2 lines required').refine(
    (lines) => {
      const totalDebit = lines.reduce((sum, line) => sum + line.debit, 0);
      const totalCredit = lines.reduce((sum, line) => sum + line.credit, 0);
      return Math.abs(totalDebit - totalCredit) < 0.01; // Allow for small rounding differences
    },
    {
      message: 'Total debits must equal total credits',
      path: ['lines'],
    }
  ),
});

// Payment Schemas
export const paymentSchema = z.object({
  amount: z.number().min(0.01, 'Amount must be greater than 0'),
  method: z.enum(['bank_transfer', 'gcash_manual', 'static_qr', 'card'], {
    errorMap: () => ({ message: 'Invalid payment method' })
  }),
  type: z.enum(['membership_fee', 'share_capital', 'loan_payment', 'store_purchase'], {
    errorMap: () => ({ message: 'Invalid payment type' })
  }),
  description: z.string().optional(),
  reference: z.string().optional(),
});

// Order and Delivery Schemas
export const orderSchema = z.object({
  items: z.array(z.object({
    productId: z.string().min(1, 'Product is required'),
    quantity: z.number().min(0.25, 'Quantity must be at least 0.25'),
    price: z.number().min(0, 'Price cannot be negative'),
  })).min(1, 'At least one item required'),
  deliveryAddress: z.string().min(1, 'Delivery address is required'),
  contactPhone: z.string().min(1, 'Contact phone is required'),
  specialInstructions: z.string().optional(),
});

// Cooperative Membership Schemas
export const coopMembershipClassSchema = z.enum(['farmer', 'consumer', 'associate', 'institution'], {
  errorMap: () => ({ message: 'Invalid membership class' })
});

export const coopMembershipStatusSchema = z.enum(['none', 'pending', 'approved', 'rejected', 'suspended'], {
  errorMap: () => ({ message: 'Invalid membership status' })
});

export const kycDocumentSchema = z.object({
  idFront: z.string().url('Invalid ID front image URL'),
  idBack: z.string().url('Invalid ID back image URL'),
  selfie: z.string().url('Invalid selfie image URL'),
  proofOfAddress: z.string().url('Invalid proof of address image URL'),
});

export const duesSchema = z.object({
  entranceFeePaid: z.boolean().default(false),
  shares: z.number().min(0, 'Shares cannot be negative').default(0),
  savings: z.number().min(0, 'Savings cannot be negative').default(0),
});

export const coopMembershipSchema = z.object({
  // Core membership info
  memberId: z.string().min(1, 'Member ID is required').max(50, 'Member ID too long').optional(),
  class: coopMembershipClassSchema,
  status: coopMembershipStatusSchema.default('pending'),
  
  // Verification
  verifiedByAdmin: z.boolean().default(false),
  verifiedAt: z.date().optional(),
  
  // KYC Documents
  kycDocs: kycDocumentSchema.optional(),
  
  // Financial
  dues: duesSchema.default({}),
  
  // Audit
  createdAt: z.date().default(() => new Date()),
  updatedAt: z.date().default(() => new Date()),
  decidedByUid: z.string().optional(),
  decidedAt: z.date().optional(),
  
  // Application details
  applicationNotes: z.string().max(1000, 'Notes too long').optional(),
  rejectionReason: z.string().max(500, 'Reason too long').optional(),
  
  // Contact info (redundant with users but useful for coop context)
  emergencyContact: z.object({
    name: z.string().min(2, 'Emergency contact name too short').max(100, 'Emergency contact name too long').optional(),
    phone: z.string().optional(),
    relationship: z.string().optional(),
  }).optional(),
});

export const coopMembershipApplicationSchema = z.object({
  // Profile fields
  firstName: z.string().min(1, 'First name is required').max(50, 'First name too long'),
  middleName: z.string().max(50, 'Middle name too long').optional(),
  lastName: z.string().min(1, 'Last name is required').max(50, 'Last name too long'),
  birthdate: z.string().min(1, 'Birthdate is required'),
  birthplace: z.string().min(1, 'Birthplace is required').max(100, 'Birthplace too long'),
  sex: z.enum(['female', 'male'], { errorMap: () => ({ message: 'Sex is required' }) }),
  civilStatus: z.enum(['single', 'married', 'widow', 'widowed', 'separated'], { errorMap: () => ({ message: 'Civil status is required' }) }),
  pwd: z.boolean().default(false),
  senior: z.boolean().default(false),
  
  // Basic info
  class: coopMembershipClassSchema,
  phone: z.string().min(1, 'Phone number is required'),
  address: z.string().min(1, 'Address is required'),
  
  // KYC Documents
  kycDocs: kycDocumentSchema,
  
  // Financial commitment
  entranceFee: z.boolean().default(false),
  shareCapital: z.number().min(0, 'Share capital cannot be negative').optional(),
  
  // Payment information
  entranceFeePayment: z.string().url('Invalid entrance fee receipt URL').optional(),
  entranceFeeAmount: z.number().min(0, 'Entrance fee amount cannot be negative').optional(),
  entranceFeePaymentStatus: z.string().optional(),
  entranceFeePaymentId: z.string().optional(), // Payment ID from payments collection
  shareCapitalPayment: z.string().url('Invalid share capital receipt URL').optional(),
  shareCapitalAmount: z.number().min(0, 'Share capital amount cannot be negative').optional(),
  shareCapitalPaymentStatus: z.string().optional(),
  shareCapitalPaymentId: z.string().optional(), // Payment ID from payments collection
  
  // Additional info
  occupation: z.string().optional(),
  businessType: z.string().optional(),
  annualIncome: z.number().min(0, 'Annual income cannot be negative').optional(),
  
  // Terms acceptance
  termsAccepted: z.boolean().refine(val => val === true, {
    message: 'You must accept the terms and conditions'
  }),
  privacyAccepted: z.boolean().refine(val => val === true, {
    message: 'You must accept the privacy policy'
  }),
});

export const deliverySchema = z.object({
  orderId: z.string().min(1, 'Order ID is required'),
  riderId: z.string().min(1, 'Rider assignment is required'),
  status: z.enum(['assigned', 'picked_up', 'in_transit', 'delivered', 'failed'], {
    errorMap: () => ({ message: 'Invalid delivery status' })
  }),
  estimatedDeliveryTime: z.string().optional(),
  actualDeliveryTime: z.string().optional(),
  notes: z.string().optional(),
});

// Search and Filter Schemas
export const searchFiltersSchema = z.object({
  query: z.string().optional(),
  category: z.string().optional(),
  minPrice: z.number().min(0, 'Minimum price cannot be negative').optional(),
  maxPrice: z.number().min(0, 'Maximum price cannot be negative').optional(),
  stockOnly: z.boolean().optional(),
  sortBy: z.enum(['new', 'price-asc', 'price-desc', 'name-asc', 'name-desc']).optional(),
  pageSize: z.number().min(8, 'Page size must be at least 8').max(100, 'Page size cannot exceed 100').optional(),
});

// Utility function to validate data against schema
export function validateData(schema, data) {
  try {
    return { success: true, data: schema.parse(data) };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const formattedErrors = error.errors.reduce((acc, err) => {
        const path = err.path.join('.');
        acc[path] = err.message;
        return acc;
      }, {});
      return { success: false, errors: formattedErrors };
    }
    return { success: false, errors: { general: 'Validation failed' } };
  }
}

// Utility function to get field error
export function getFieldError(errors, fieldName) {
  return errors?.[fieldName] || '';
}

export default {
  userProfileSchema,
  loginSchema,
  signupSchema,
  productSchema,
  accountSchema,
  journalEntrySchema,
  paymentSchema,
  orderSchema,
  deliverySchema,
  searchFiltersSchema,
  validateData,
  getFieldError,
};
